import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useStructuraAuth } from './useStructuraAuth'
import { useTranslation } from '@structura/i18n'

export interface AuthNavActionsProps {
  // Per-app beam designer route (e.g. "/beam/wood"). The signed-in dropdown
  // includes a "Beam Designer" shortcut linking here. Typically forwarded
  // from the consuming Navbar's beamRoute prop.
  beamRoute: string
}

const AuthNavActions: React.FC<AuthNavActionsProps> = ({ beamRoute }) => {
  const authState = useStructuraAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleLogin = React.useCallback(async (): Promise<void> => {
    navigate('/account')
  }, [navigate])

  const handleRegister = React.useCallback(async (): Promise<void> => {
    navigate('/register')
  }, [navigate])

  const handleLogout = React.useCallback((): void => {
    authState.logout()
    navigate('/')
  }, [authState, navigate])

  if (!authState.isSupported) {
    return (
      <>
        <li className="nav-item">
          <Link className="nav-link" to="/register">
            <i className="bi bi-heart-fill me-1"></i>
            {t('auth.nav.register')}
          </Link>
        </li>
        <li className="nav-item ms-lg-3">
          <Link className="btn btn-primary px-4" to="/register">
            {t('auth.nav.previewRegister')}
          </Link>
        </li>
      </>
    )
  }

  if (authState.isLoading) {
    return (
      <li className="nav-item ms-lg-3">
        <span className="nav-link disabled">
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {t('auth.common.loading')}
        </span>
      </li>
    )
  }

  if (!authState.isAuthenticated) {
    // 'Start Free Trial' and 'Register' both navigated to /register; the
    // duplicate was removed in favour of the shorter, neutral label.
    return (
      <>
        <li className="nav-item">
          <button type="button" className="nav-link btn btn-link" onClick={handleRegister}>
            <i className="bi bi-heart-fill me-1"></i>
            {t('auth.nav.register')}
          </button>
        </li>
        <li className="nav-item ms-lg-3">
          <button type="button" className="btn btn-register-primary px-4" onClick={handleLogin}>
            <i className="bi bi-box-arrow-in-right me-1"></i>
            {t('auth.nav.signIn')}
          </button>
        </li>
      </>
    )
  }

  return (
    <>
      <li className="nav-item dropdown">
        <button
          className="nav-link dropdown-toggle d-flex align-items-center"
          id="authDropdown"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          type="button"
        >
          {authState.userPicture !== undefined ? (
            <img
              src={authState.userPicture}
              alt={authState.userName ?? t('auth.nav.accountAlt')}
              className="rounded-circle me-2"
              style={{ width: '32px', height: '32px', objectFit: 'cover' }}
            />
          ) : (
            <i className="bi bi-person-circle me-2"></i>
          )}
          <span>{authState.userName ?? authState.userEmail ?? t('auth.nav.myAccount')}</span>
        </button>
        <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="authDropdown">
          <li>
            <Link className="dropdown-item" to="/account">
              <i className="bi bi-person-badge me-2"></i>
              {t('auth.nav.accountDashboard')}
            </Link>
          </li>
          <li>
            <Link className="dropdown-item" to={beamRoute}>
              <i className="bi bi-diagram-3 me-2"></i>
              {t('auth.nav.beamDesigner')}
            </Link>
          </li>
          <li><hr className="dropdown-divider" /></li>
          <li>
            <button type="button" className="dropdown-item" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right me-2"></i>
              {t('auth.nav.signOut')}
            </button>
          </li>
        </ul>
      </li>
    </>
  )
}

export default AuthNavActions


